import React from 'react';
import { useCSVReader } from 'react-papaparse';
import SecurityIcon from '@mui/icons-material/Security';
import { Button } from '@mui/material';
import { showMessage } from '../utils/showMessage';

interface IronDomeImporterProps {
  type: 'offer' | 'campaign';
  importFunction: (csvRecords: any[]) => Promise<any>;
  setLoading: (loading: boolean) => void;
  fetchData: () => void;
}

function IronDomeImporter({ 
  type, 
  importFunction, 
  setLoading, 
  fetchData 
}: IronDomeImporterProps) {
  const { CSVReader } = useCSVReader();

  const handleIronDomeImport = async (csvData: any[]) => {
    console.log('Import Iron Dome CSV data:', csvData);
    
    if (csvData.length === 0) {
      showMessage({ message: 'No data found in CSV file', severity: 'error' });
      return;
    }

    const headers = csvData[0];
    const entityName = type === 'offer' ? 'offerName' : 'campaignName';
    const requiredHeaders = [entityName, 'blockAnon', 'blockDataCenter', 'blockIpGeneral', 'blockIpDdos', 'blockIpRecon'];
    
    const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
    if (missingHeaders.length > 0) {
      showMessage({ 
        message: `Missing required headers: ${missingHeaders.join(', ')}`, 
        severity: 'error' 
      });
      return;
    }

    const csvRecords = [];
    for (let i = 1; i < csvData.length; i++) {
      const row = csvData[i];
      if (row.length === 0 || row.every((cell: any) => !cell || cell.trim() === '')) {
        continue;
      }

      const csvRecord: any = {};
      headers.forEach((header: string, index: number) => {
        csvRecord[header] = row[index] || '';
      });

      csvRecords.push(csvRecord);
    }

    if (csvRecords.length === 0) {
      showMessage({ message: 'No valid rows found to import', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await importFunction(csvRecords);
      const entityPlural = type === 'offer' ? 'offers' : 'campaigns';
      const processedCount = response.result[`processed${type === 'offer' ? 'Offers' : 'Campaigns'}`];
      
      showMessage({ 
        message: `Successfully updated ${processedCount} ${entityPlural} with Iron Dome rules.`, 
        severity: 'success' 
      });
      fetchData();
    } catch (err: any) {
      console.error('Error importing Iron Dome rules:', err);
      
      const entityPlural = type === 'offer' ? 'offers' : 'campaigns';
      const notFoundKey = `notFound${type === 'offer' ? 'Offers' : 'Campaigns'}`;
      
      if (err.response?.data?.[notFoundKey]) {
        showMessage({ 
          message: `The following ${entityPlural} were not found: ${err.response.data[notFoundKey].join(', ')}`, 
          severity: 'error' 
        });
      } else if (err.response?.data?.error) {
        showMessage({ 
          message: `Error: ${err.response.data.error}`, 
          severity: 'error' 
        });
      } else {
        showMessage({ 
          message: 'An error occurred while importing Iron Dome rules.', 
          severity: 'error' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <CSVReader
      onUploadAccepted={(data: any) => {
        handleIronDomeImport(data.data);
      }}
    >
      {({ getRootProps }: any) => (
        <Button {...getRootProps()} size={type === 'offer' ? 'small' : 'medium'} startIcon={<SecurityIcon />}>
          {type === 'offer' ? 'Import Iron Dome' : 'Iron Dome Import'}
        </Button>
      )}
    </CSVReader>
  );
}

export default IronDomeImporter;